import { FieldMappingFieldItem } from '@components/text-editors/addons/field-mapping';

import type {
  ArrayLocationOption,
  ListSyncTestRequestType,
  MappingKey,
} from '../integrations-types';

const VARIABLE_PATTERN = /{{\s*([^}]+?)\s*}}/g;

const PLACEHOLDER_PATTERN = /{{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g;

export const createLabelFromKey = (name: string): string =>
  name
    .replace(/_/g, ' ')
    .replace(/(?<!^)([A-Z][a-z])/g, ' $1')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const extractUrlPlaceholders = (url: string): string[] => {
  const added = new Set<string>();
  const result: string[] = [];
  for (const match of url.matchAll(PLACEHOLDER_PATTERN)) {
    const name = match[1];
    if (!added.has(name)) {
      added.add(name);
      result.push(name);
    }
  }
  return result;
};

export const substituteParametersIntoUrl = (
  url: string,
  parameterValues: Record<string, string>,
): string =>
  url.replace(PLACEHOLDER_PATTERN, (match, name) => {
    const value = parameterValues[name];
    return value !== undefined && value !== '' ? value : match;
  });

/**
 * Converts a mapping label template from the API (e.g. "{{name}} - {{details[category]}}")
 * into a FieldMappingTextEditor-compatible default value with type-suffixed variable keys.
 *
 * Returns { value, variables } where:
 * - value: template with type-suffixed keys, e.g. "{{name_text}} - {{details[category]_text}}"
 * - variables: map from suffixed key to the matching field item
 */
export const buildMappingLabelDefaultValue = (
  mappingLabel: string,
  fields: FieldMappingFieldItem[],
): { value: string; variables: Record<string, FieldMappingFieldItem> } => {
  const getTypeSuffix = (key: string): string => {
    const field = fields.find((f) => f.label === key);
    return field?.type ?? 'text';
  };

  const value = mappingLabel.replace(
    VARIABLE_PATTERN,
    (_, key) => `{{${key}_${getTypeSuffix(key)}}}`,
  );
  const variables = Array.from(mappingLabel.matchAll(VARIABLE_PATTERN)).reduce(
    (acc, [, key]) => {
      const field = fields.find((f) => f.label === key);
      if (field) {
        acc[`${key}_${getTypeSuffix(key)}`] = field;
      }
      return acc;
    },
    {} as Record<string, FieldMappingFieldItem>,
  );

  return { value, variables };
};

/**
 * Extract path from endpoint URL by removing the base URL prefix.
 */
export const getPathFromEndpointUrl = (baseUrl: string, endpointUrl?: string): string => {
  const url = endpointUrl ?? '';
  if (!url) return '';

  const baseUrlTrimmed = baseUrl ? `${baseUrl.replace(/\/+$/, '')}/` : '';
  if (baseUrlTrimmed && url.startsWith(baseUrlTrimmed)) {
    return url.slice(baseUrlTrimmed.length);
  }
  if (baseUrl && url.startsWith(baseUrl)) {
    return url.slice(baseUrl.length).replace(/^\/+/, '');
  }
  // Fallback: if backend returned a full URL unrelated to baseUrl, or already stored a path.
  return url.replace(/^\/+/, '');
};

/**
 * Combine base URL and path into full endpoint URL.
 */
export const buildEndpointUrl = (baseUrl: string, path: string): string => {
  const baseUrlTrimmed = baseUrl ? baseUrl.replace(/\/+$/, '') : '';
  const pathTrimmed = path ? path.replace(/^\/+/, '') : '';
  return baseUrlTrimmed ? `${baseUrlTrimmed}/${pathTrimmed}` : pathTrimmed;
};

/**
 * Extract a value from nested data using a dot-separated path.
 * Example: extractValueFromPath({ response_metadata: { next_cursor: "abc" } }, "response_metadata.next_cursor") => "abc"
 */
export const extractValueFromPath = (data: unknown, path: string): unknown => {
  if (!data || !path) return undefined;
  let value: unknown = data;
  for (const key of path.split('.')) {
    if (value && typeof value === 'object' && key in value) {
      value = (value as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return value;
};

/**
 * Convert dot notation to bracket notation for display.
 * Example: "response_metadata.next_cursor" => "response_metadata[next_cursor]"
 */
export const dotToBracketNotation = (path: string): string => {
  const parts = path.split('.');
  if (parts.length <= 1) return path;
  return `${parts[0]}${parts
    .slice(1)
    .map((p) => `[${p}]`)
    .join('')}`;
};

/**
 * Build a paginated URL by adding a query parameter with the token value.
 * Example: buildPaginatedUrl("https://api.example.com/users", "cursor", "abc123")
 *          => "https://api.example.com/users?cursor=abc123"
 */
export const buildPaginatedUrl = (
  baseUrl: string,
  paramName: string,
  tokenValue: string,
): string => {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set(paramName, tokenValue);
    return url.toString();
  } catch {
    // If URL is invalid, just append the query param
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${encodeURIComponent(paramName)}=${encodeURIComponent(
      tokenValue,
    )}`;
  }
};

export type FlattenedPath = {
  path: string;
  value: unknown;
  type: 'string' | 'number' | 'boolean' | 'null' | 'object' | 'array';
};

const getType = (value: unknown): FlattenedPath['type'] => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value as FlattenedPath['type'];
};

/**
 * Flatten an object into dot-separated paths with values and types.
 */
export const flattenObjectToPaths = (
  data: unknown,
  prefix = '',
  results: FlattenedPath[] = [],
): FlattenedPath[] => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    if (prefix) results.push({ path: prefix, value: data, type: getType(data) });
    return results;
  }

  if (prefix) results.push({ path: prefix, value: data, type: 'object' });

  for (const [key, value] of Object.entries(data)) {
    flattenObjectToPaths(value, prefix ? `${prefix}.${key}` : key, results);
  }

  return results;
};

export const hasMultipleArrayLocations = (
  testResponse: ListSyncTestRequestType | null | undefined,
): boolean => {
  return Boolean(testResponse?.arrayLocations && testResponse.arrayLocations.length > 1);
};

/**
 * Split an array location on its unescaped dots, unescaping each segment.
 * Mirrors `split_array_path` on the backend, which escapes response keys
 * holding a dot so they survive as a single segment.
 */
export const splitArrayLocation = (path: string): string[] => {
  const segments: string[] = [];
  let current = '';
  let index = 0;
  while (index < path.length) {
    const character = path[index];
    if (character === '\\' && index + 1 < path.length) {
      current += path[index + 1];
      index += 2;
      continue;
    }
    if (character === '.') {
      segments.push(current);
      current = '';
      index += 1;
      continue;
    }
    current += character;
    index += 1;
  }
  segments.push(current);
  return segments;
};

/**
 * Turn an array-location path into a human-readable label.
 * Example: "sections.issues" => "Sections → Issues"
 */
export const formatArrayLocationLabel = (path: string): string => {
  return splitArrayLocation(path)
    .map((segment) => {
      return createLabelFromKey(segment);
    })
    .join(' → ');
};

/**
 * The location currently in effect: a picker selection, else the one the test
 * request auto-selected.
 *
 * The selection is seeded from the saved list when the sidebar opens, so a
 * truthy value here does not mean the user chose anything this session. Callers
 * that write it back have to establish that separately.
 */
const effectiveArrayLocation = (
  selectedArrayLocation: string | undefined,
  testResponse: ListSyncTestRequestType | null | undefined,
): string | undefined => {
  const resolved = selectedArrayLocation ?? testResponse?.arrayLocation;
  return resolved || undefined;
};

/**
 * The array-location fields to submit, or undefined to leave the stored ones as
 * they are.
 *
 * Locking suppresses the backend's auto-detection, so it is only sent when that
 * detection could not arrive at the same answer by itself: the response offered
 * a choice, or the location is nested and detection only looks one level deep.
 *
 * Nothing is sent without a test response in this session — the picker is not
 * rendered without one, so there is no selection to save, and submitting the
 * stored location would turn the lock on for a list that was left unlocked.
 */
export const buildArrayLocationPayload = (
  selectedArrayLocation: string | undefined,
  testResponse: ListSyncTestRequestType | null | undefined,
): { arrayLocation: string; isArrayLocationLocked: true } | undefined => {
  if (!testResponse) {
    return undefined;
  }
  const location = effectiveArrayLocation(selectedArrayLocation, testResponse);
  if (!location) {
    return undefined;
  }
  const needsLock =
    hasMultipleArrayLocations(testResponse) || splitArrayLocation(location).length > 1;
  if (!needsLock) {
    return undefined;
  }
  return { arrayLocation: location, isArrayLocationLocked: true };
};

/**
 * Whether the effective location points at an array nested under another one,
 * which the sync flattens — so one item reachable through two parents becomes a
 * single option.
 */
export const isNestedArrayLocation = (
  selectedArrayLocation: string | undefined,
  testResponse: ListSyncTestRequestType | null | undefined,
): boolean => {
  const resolved = effectiveArrayLocation(selectedArrayLocation, testResponse);
  if (!resolved) {
    return false;
  }
  return splitArrayLocation(resolved).length > 1;
};

// The only collection types the backend produces: get_value_type returns
// "array_of_strings" or "array" for a list, and a nested object never reaches it
// because extract_mapping_keys recurses into one and flattens it to about[name].
const COLLECTION_MAPPING_TYPES = new Set(['array', 'array_of_strings']);

/**
 * Keep only single-value keys for the label/value pickers. An option is a
 * `{ label, value }` pair holding one value, so an array-typed key is never a
 * valid target.
 */
export const filterSingleValueMappingKeys = (keys: MappingKey[]): MappingKey[] => {
  return keys.filter((key) => {
    return !COLLECTION_MAPPING_TYPES.has(key.type);
  });
};

export const getMappingKeysForArrayLocation = (
  testResponse: ListSyncTestRequestType | null | undefined,
  selectedArrayLocation: string | undefined,
  fallbackMappingKeys: MappingKey[] | undefined,
): MappingKey[] => {
  if (testResponse?.arrayLocations && testResponse.arrayLocations.length > 0) {
    const match = testResponse.arrayLocations.find(
      (option: ArrayLocationOption) => option.name === selectedArrayLocation,
    );
    return match?.mappingKeys ?? [];
  }
  return testResponse?.mappingKeys ?? fallbackMappingKeys ?? [];
};

// Undo the endpoint's { root: [...] } wrapping — off the flag, not the shape.
export const unwrapRoot = (exampleData: object, isWrapped: boolean): object => {
  if (!isWrapped) {
    return exampleData;
  }
  const root = (exampleData as Record<string, unknown>)['root'];
  return Array.isArray(root) ? root : exampleData;
};
